import "@fontsource/barlow-condensed/600.css";
import "@fontsource/barlow-condensed/700.css";
import "@fontsource/barlow-condensed/800.css";
import "@fontsource/barlow-condensed/900.css";
import "@fontsource/azeret-mono/400.css";
import "@fontsource/azeret-mono/600.css";
import "@fontsource/azeret-mono/700.css";
import "./style.css";

type SystemMode = "idle" | "tuning" | "ready" | "running" | "complete";

type MicroStepRule = {
  pattern: RegExp;
  step: (task: string) => string;
};

const icon = (name: "power" | "signal" | "arrow" | "check" | "bolt") => {
  const paths = {
    power: '<path d="M12 2.7v8.2M6.2 6.2a7.2 7.2 0 1 0 11.6 0"/>',
    signal: '<path d="M4 18h2M9 14h2v4H9zM14 9h2v9h-2zM19 4h2v14h-2z"/>',
    arrow: '<path d="M5 12h13M14 7l5 5-5 5"/>',
    check: '<path d="m5 12 4 4L19 6"/>',
    bolt: '<path d="M13.5 2 5 13h6l-.5 9L19 10h-6z"/>',
  };
  return `<svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="square" stroke-linejoin="miter">${paths[name]}</svg>`;
};

const app = document.querySelector<HTMLDivElement>("#app");

if (!app) throw new Error("Sovelluksen juurielementti puuttuu.");

app.innerHTML = `
  <div class="station" data-mode="idle">
    <header class="station-header">
      <a class="wordmark" href="#top" aria-label="Motinet.fi, etusivu">
        <span class="wordmark-main">motinet.fi</span>
        <span class="wordmark-sub">motivaation verkkopalvelu</span>
      </a>
      <div class="header-status" aria-label="Palvelun tila">
        <span class="status-lamp" aria-hidden="true"></span>
        <span id="network-label">OSITTAINEN HÄIRIÖ</span>
      </div>
      <div class="clock" aria-label="Paikallinen aika">
        <span>HEL</span>
        <time id="station-clock">--:--:--</time>
      </div>
      <a class="mobile-jump" href="#restart-form">KÄYNNISTÄ ${icon("arrow")}</a>
    </header>

    <main id="top">
      <section class="hero-console" aria-labelledby="service-title">
        <div class="broadcast-field">
          <canvas id="signal-canvas" aria-hidden="true"></canvas>
          <div class="scope-grid" aria-hidden="true"></div>
          <div class="broadcast-copy">
            <h1 id="service-title">Motivaatio<br />ei vastaa.</h1>
            <p class="service-lead">Yhteys omaan tekemiseen katkesi odottamattomasti. Vika voidaan kiertää yhdellä riittävän pienellä liikkeellä.</p>
          </div>

          <div class="signal-dial" aria-label="Motivaation signaalitaso">
            <div class="dial-ticks" aria-hidden="true"></div>
            <div class="dial-core">
              <span class="dial-label">MOTI</span>
              <strong id="signal-percent">0</strong>
              <span class="dial-unit">%</span>
              <span class="dial-state" id="dial-state">EI KANTOALTOA</span>
            </div>
            <div class="sweep-arm" aria-hidden="true"></div>
          </div>

          <div class="broadcast-stamp" aria-hidden="true">
            <span>LÄHETYS</span>
            <strong>M-503</strong>
            <span>KANAVA MOTI</span>
          </div>
        </div>

        <div class="operator-console">
          <div class="console-plate">
            <h2>Käynnistä moti uudelleen</h2>
            <div class="console-id-cluster">
              <div class="console-lamps" aria-label="Palautusyksikön tilat">
                <span class="lamp lamp-red is-on"><span>VIKA</span></span>
                <span class="lamp lamp-yellow"><span>VIRITYS</span></span>
                <span class="lamp lamp-blue"><span>LINJA</span></span>
              </div>
              <span class="plate-id">PR-2 / 230 V / FIN</span>
            </div>
          </div>

          <div class="console-workspace">
            <form id="restart-form" novalidate>
              <label for="task-input">Mitä olet yrittänyt vältellä?</label>
              <div class="input-well">
                <input
                  id="task-input"
                  name="task"
                  type="text"
                  maxlength="140"
                  autocomplete="off"
                  placeholder="esim. vastaa siihen sähköpostiin"
                  aria-describedby="task-help task-error"
                />
                <span class="input-counter"><span id="character-count">0</span>/140</span>
              </div>
              <p id="task-help" class="field-help">Tieto ei poistu tästä selaimesta. Järjestelmä pilkkoo sen vain pienemmäksi.</p>
              <p id="task-error" class="field-error" role="alert"></p>

              <div class="preset-rail" aria-label="Esimerkkitehtävät">
                <button type="button" class="preset" data-preset="vastaa sähköpostiin">SÄHKÖPOSTI</button>
                <button type="button" class="preset" data-preset="aloita siivoaminen">SIIVOUS</button>
                <button type="button" class="preset" data-preset="avaa työdokumentti">TYÖ</button>
              </div>

              <button
                class="restart-switch restart-switch--compact"
                id="restart-button"
                type="submit"
                aria-controls="microstep"
                aria-expanded="false"
              >
                <span class="switch-copy-polish">
                  <strong class="switch-command"><span>KÄYNNISTÄ MOTI</span><span>UUDELLEEN</span></strong>
                  <span class="switch-detail">Arvioitu palautusaika 2 min</span>
                </span>
              </button>
            </form>

            <section class="microstep" id="microstep" role="region" aria-live="polite" aria-labelledby="microstep-text" hidden>
              <div class="microstep-topline">
                <span>VÄLIAIKAINEN OHITUSREITTI</span>
                <span id="microstep-code">ASKEL 01</span>
              </div>
              <p>Älä tee koko tehtävää. Tee nyt vain tämä:</p>
              <h3 id="microstep-text"></h3>
              <div class="session-controls">
                <button id="session-button" class="session-button" type="button">
                  <span id="session-button-icon">${icon("bolt")}</span>
                  <span id="session-button-label">ALOITA 02:00</span>
                </button>
                <output id="session-timer" class="session-timer" aria-label="Jäljellä oleva aika">02:00</output>
              </div>
              <button id="new-task-button" class="text-button" type="button">Vaihda tehtävää</button>
            </section>
          </div>
        </div>
      </section>

      <section class="telemetry-ribbon" aria-label="Järjestelmän tapahtumaloki">
        <div class="telemetry-heading">
          <span>${icon("signal")}</span>
          <strong>LIVE / MOTI-TELEMETRIA</strong>
        </div>
        <div class="telemetry-window" aria-live="polite">
          <div id="telemetry-track" class="telemetry-track">
            <span>09:42:01 KANTOALTO KADONNUT</span>
            <span>09:42:02 TEKOSYYPALVELIN VASTAA NORMAALISTI</span>
            <span>09:42:04 HUOMINEN YLIKUORMITETTU</span>
          </div>
        </div>
        <div class="telemetry-rate"><span id="packet-rate">0.0</span> MOT/s</div>
      </section>

      <section class="systems-board" aria-labelledby="systems-title">
        <div class="board-heading">
          <h2 id="systems-title">Järjestelmän tilannekuva</h2>
          <p>Automaattinen analyysi on suoritettu ilman minkäänlaista pätevyyttä.</p>
        </div>
        <div class="status-lines">
          <div class="status-line" data-status="major">
            <span class="line-index">A1</span>
            <div><strong>Aloittaminen</strong><span>Ensimmäinen liike ei läpäise palomuuria</span></div>
            <span class="line-status">VAKAVA HÄIRIÖ</span>
            <span class="line-meter"><i style="--level: 12%"></i></span>
          </div>
          <div class="status-line" data-status="unstable">
            <span class="line-index">K7</span>
            <div><strong>Keskittymiskyky</strong><span>Paketteja katoaa muiden välilehtien suuntaan</span></div>
            <span class="line-status">EPÄVAKAA</span>
            <span class="line-meter"><i style="--level: 34%"></i></span>
          </div>
          <div class="status-line" data-status="online">
            <span class="line-index">T9</span>
            <div><strong>Tekosyyt</strong><span>Kaikki 48 varapalvelinta käytettävissä</span></div>
            <span class="line-status">TOIMINNASSA</span>
            <span class="line-meter"><i style="--level: 100%"></i></span>
          </div>
          <div class="status-line" data-status="queued">
            <span class="line-index">H0</span>
            <div><strong>Huominen</strong><span>Pyyntöjä jonossa enemmän kuin suositellaan</span></div>
            <span class="line-status">YLIKUORMITETTU</span>
            <span class="line-meter"><i style="--level: 78%"></i></span>
          </div>
        </div>
      </section>

      <section class="utility-bay" aria-labelledby="utility-title">
        <div class="utility-intro">
          <h2 class="utility-title-stack" id="utility-title" aria-label="Kenttädiagnostiikka"><span>KENTTÄ</span><span>DIAGNOSTIIKKA</span></h2>
          <p>Kaksi epätieteellistä mittalaitetta tilanteisiin, joissa varsinaisen asian tekeminen tuntuu vielä kohtuuttomalta.</p>
        </div>

        <article class="speed-apparatus">
          <div class="apparatus-header">
            <h3>Motivaation nopeustesti</h3>
            <span>MITTALAITE N-2</span>
          </div>
          <div class="speed-readout">
            <output id="speed-value">0.0</output>
            <span>MOT/s</span>
            <div class="speed-bar" aria-hidden="true"><i id="speed-fill"></i></div>
          </div>
          <p id="speed-verdict">Yhteyttä ei ole vielä mitattu.</p>
          <button id="speed-button" class="apparatus-button" type="button">SUORITA MITTAUS ${icon("signal")}</button>
        </article>

        <article class="excuse-apparatus">
          <div class="apparatus-header">
            <h3>Tekosyygeneraattori</h3>
            <span>VARAJÄRJESTELMÄ TX-48</span>
          </div>
          <blockquote id="excuse-output">“En voi aloittaa vielä, koska en ole päättänyt missä järjestyksessä vältän tätä.”</blockquote>
          <button id="excuse-button" class="apparatus-button" type="button">HAE UUSI TEKOSYY ${icon("arrow")}</button>
        </article>
      </section>
    </main>

    <footer class="station-footer">
      <div>
        <strong>motinet.fi</strong>
        <span>Motivaation verkkopalvelu</span>
      </div>
      <p>Palvelu ei lupaa motivaatiota. Se lupaa yhden tarpeeksi pienen alun.</p>
      <a href="#top">PALAA VALVOMOON ${icon("arrow")}</a>
    </footer>
  </div>
`;

const $ = <T extends Element>(selector: string) => {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Elementtiä ei löytynyt: ${selector}`);
  return element;
};

const station = $<HTMLElement>(".station");
const form = $<HTMLFormElement>("#restart-form");
const taskInput = $<HTMLInputElement>("#task-input");
const taskError = $<HTMLParagraphElement>("#task-error");
const characterCount = $("#character-count");
const restartButton = $<HTMLButtonElement>("#restart-button");
const signalPercent = $("#signal-percent");
const dialState = $("#dial-state");
const microstep = $<HTMLElement>("#microstep");
const microstepText = $("#microstep-text");
const microstepCode = $("#microstep-code");
const sessionButton = $<HTMLButtonElement>("#session-button");
const sessionButtonLabel = $("#session-button-label");
const sessionButtonIcon = $("#session-button-icon");
const sessionTimer = $<HTMLOutputElement>("#session-timer");
const newTaskButton = $<HTMLButtonElement>("#new-task-button");
const telemetryTrack = $("#telemetry-track");
const packetRate = $("#packet-rate");
const clock = $<HTMLTimeElement>("#station-clock");
const networkLabel = $("#network-label");
const consoleLamps = Array.from(document.querySelectorAll<HTMLElement>(".lamp"));
const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

let mode: SystemMode = "idle";
let timerId: number | undefined;
let remainingSeconds = 120;
let currentTask = "";

const microStepRules: MicroStepRule[] = [
  { pattern: /sähköposti|mail|viesti|vastaa/i, step: () => "Avaa viesti ja kirjoita vain ensimmäinen lause." },
  { pattern: /siivo|järjest|tiski|pyyk/i, step: () => "Laita yksi näkyvä tavara omalle paikalleen." },
  { pattern: /koodi|ohjelm|bug|tiedosto/i, step: () => "Avaa oikea tiedosto ja nimeä seuraava muutos yhdellä kommentilla." },
  { pattern: /kirjo|raport|essee|dokument/i, step: () => "Avaa dokumentti ja kirjoita otsikon alle yksi huono lause." },
  { pattern: /soita|puhelu/i, step: () => "Avaa yhteystieto. Älä vielä soita." },
  { pattern: /liiku|lenk|treen|sali|juok/i, step: () => "Laita kengät jalkaan. Se riittää seuraavaksi askeleeksi." },
  { pattern: /lue|kirja|artikkeli/i, step: () => "Avaa teksti ja lue ensimmäinen kokonainen kappale." },
  { pattern: /lasku|vero|hakemus|lomake/i, step: () => "Avaa lomake ja täytä vain ensimmäinen tyhjä kenttä." },
];

const excuses = [
  "En voi aloittaa vielä, koska kello on jo epäilyttävän paljon.",
  "Tarvitsen ensin paremman järjestelmän tämän aloittamisen suunnitteluun.",
  "Aloitan heti kun tämä yksi täysin asiaan liittymätön välilehti on käsitelty.",
  "Tehtävä vaikuttaa tällä hetkellä palvelinpuolen ongelmalta.",
  "Motivaatio on kyllä matkalla, mutta se valitsi hitaan toimituksen.",
  "En halua häiritä tehtävää kesken sen rauhallisen odottamisen.",
  "Nykyinen tuolikulma ei teknisesti tue aloittamista.",
];

const formatTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60).toString().padStart(2, "0");
  const secs = (seconds % 60).toString().padStart(2, "0");
  return `${minutes}:${secs}`;
};

const setMode = (nextMode: SystemMode) => {
  mode = nextMode;
  station.setAttribute("data-mode", mode);
  consoleLamps.forEach((lamp) => lamp.classList.remove("is-on"));
  const lampIndex = mode === "tuning" ? 1 : mode === "ready" || mode === "running" || mode === "complete" ? 2 : 0;
  consoleLamps[lampIndex]?.classList.add("is-on");
};

const pushTelemetry = (message: string) => {
  const timestamp = new Date().toLocaleTimeString("fi-FI", { hour12: false });
  const line = document.createElement("span");
  line.textContent = `${timestamp} ${message.toUpperCase()}`;
  telemetryTrack.prepend(line);
  while (telemetryTrack.children.length > 5) telemetryTrack.lastElementChild?.remove();
};

const deriveMicroStep = (task: string) => {
  const match = microStepRules.find((rule) => rule.pattern.test(task));
  return match?.step(task) ?? `Avaa asia, johon “${task.trim()}” liittyy. Tee ensimmäinen näkyvä liike.`;
};

const animateSignal = (target: number, duration = 1800) => {
  const startValue = Number(signalPercent.textContent) || 0;
  const startTime = performance.now();
  const actualDuration = prefersReducedMotion.matches ? 1 : duration;

  return new Promise<void>((resolve) => {
    const tick = (now: number) => {
      const elapsed = Math.min((now - startTime) / actualDuration, 1);
      const eased = 1 - Math.pow(1 - elapsed, 4);
      const value = Math.round(startValue + (target - startValue) * eased);
      signalPercent.textContent = String(value);
      station.style.setProperty("--signal", `${value}%`);
      packetRate.textContent = (value / 37).toFixed(1);
      if (elapsed < 1) requestAnimationFrame(tick);
      else resolve();
    };
    requestAnimationFrame(tick);
  });
};

const beginRecovery = async () => {
  currentTask = taskInput.value.trim();
  if (!currentTask) {
    taskError.textContent = "Kirjoita yksi asia, jota järjestelmän pitäisi yrittää käynnistää.";
    taskInput.setAttribute("aria-invalid", "true");
    taskInput.focus();
    return;
  }

  taskError.textContent = "";
  taskInput.removeAttribute("aria-invalid");
  restartButton.disabled = true;
  taskInput.disabled = true;
  setMode("tuning");
  dialState.textContent = "VIRITETÄÄN";
  networkLabel.textContent = "PALAUTUS KÄYNNISSÄ";
  pushTelemetry(`Palautuspyyntö vastaanotettu: ${currentTask}`);

  await animateSignal(62, 2200);
  microstepText.textContent = deriveMicroStep(currentTask);
  microstepCode.textContent = `ASKEL ${String(Math.floor(Math.random() * 89) + 10)}`;
  microstep.hidden = false;
  restartButton.setAttribute("aria-expanded", "true");
  sessionButton.focus({ preventScroll: true });
  form.inert = true;
  form.setAttribute("aria-hidden", "true");
  setMode("ready");
  dialState.textContent = "OHITUSREITTI LÖYTYI";
  networkLabel.textContent = "VÄLIAIKAINEN YHTEYS";
  pushTelemetry("Ohitusreitti vahvistettu — odottaa käyttäjää");
  microstep.scrollIntoView({ behavior: prefersReducedMotion.matches ? "auto" : "smooth", block: "nearest" });
};

const finishSession = async () => {
  if (timerId) window.clearInterval(timerId);
  timerId = undefined;
  remainingSeconds = 0;
  sessionTimer.value = "00:00";
  sessionTimer.textContent = "00:00";
  setMode("complete");
  dialState.textContent = "KANTOALTO PALAUTETTU";
  networkLabel.textContent = "YHTEYS TOIMII";
  sessionButtonLabel.textContent = "YHTEYS PALAUTETTU";
  sessionButtonIcon.innerHTML = icon("check");
  sessionButton.disabled = true;
  pushTelemetry("Moti vastaa — yhteys palautettu");
  await animateSignal(100, 1400);
};

const startSession = () => {
  if (mode === "running") {
    void finishSession();
    return;
  }

  remainingSeconds = 120;
  sessionTimer.value = formatTime(remainingSeconds);
  sessionTimer.textContent = formatTime(remainingSeconds);
  sessionButtonLabel.textContent = "MERKITSE VALMIIKSI";
  sessionButtonIcon.innerHTML = icon("check");
  setMode("running");
  dialState.textContent = "KANTOALTO LUKITTU";
  pushTelemetry("Kahden minuutin lähetysikkuna avattu");
  void animateSignal(84, 1200);

  timerId = window.setInterval(() => {
    remainingSeconds -= 1;
    sessionTimer.value = formatTime(remainingSeconds);
    sessionTimer.textContent = formatTime(remainingSeconds);
    if (remainingSeconds <= 0) void finishSession();
  }, 1000);
};

const resetConsole = () => {
  if (timerId) window.clearInterval(timerId);
  timerId = undefined;
  remainingSeconds = 120;
  currentTask = "";
  taskInput.disabled = false;
  taskInput.value = "";
  characterCount.textContent = "0";
  restartButton.disabled = false;
  microstep.hidden = true;
  form.inert = false;
  form.removeAttribute("aria-hidden");
  restartButton.setAttribute("aria-expanded", "false");
  sessionButton.disabled = false;
  sessionButtonLabel.textContent = "ALOITA 02:00";
  sessionButtonIcon.innerHTML = icon("bolt");
  sessionTimer.value = "02:00";
  sessionTimer.textContent = "02:00";
  signalPercent.textContent = "0";
  station.style.setProperty("--signal", "0%");
  packetRate.textContent = "0.0";
  dialState.textContent = "EI KANTOALTOA";
  networkLabel.textContent = "OSITTAINEN HÄIRIÖ";
  setMode("idle");
  pushTelemetry("Palautusyksikkö nollattu");
  taskInput.focus();
};

form.addEventListener("submit", (event) => {
  event.preventDefault();
  void beginRecovery();
});

taskInput.addEventListener("input", () => {
  characterCount.textContent = String(taskInput.value.length);
  if (taskInput.value.trim()) {
    taskError.textContent = "";
    taskInput.removeAttribute("aria-invalid");
  }
});

document.querySelectorAll<HTMLButtonElement>(".preset").forEach((button) => {
  button.addEventListener("click", () => {
    taskInput.value = button.dataset.preset ?? "";
    characterCount.textContent = String(taskInput.value.length);
    taskInput.focus();
  });
});

sessionButton.addEventListener("click", startSession);
newTaskButton.addEventListener("click", resetConsole);

const speedButton = $<HTMLButtonElement>("#speed-button");
const speedValue = $<HTMLOutputElement>("#speed-value");
const speedFill = $<HTMLElement>("#speed-fill");
const speedVerdict = $("#speed-verdict");

speedButton.addEventListener("click", () => {
  speedButton.disabled = true;
  speedButton.textContent = "MITATAAN…";
  speedVerdict.textContent = "Lähetetään kolme täysin tarpeetonta testipakettia.";
  speedFill.style.setProperty("--speed", "0.18");

  window.setTimeout(() => {
    const result = Math.round((0.4 + Math.random() * 2.4) * 10) / 10;
    speedValue.value = result.toFixed(1);
    speedValue.textContent = result.toFixed(1);
    speedFill.style.setProperty("--speed", String(Math.min(result / 3, 1)));
    speedVerdict.textContent = result < 1
      ? "Riittää juuri ja juuri tiedoston avaamiseen. Erinomainen alku."
      : result < 2
        ? "Vakaa perusyhteys. Älä pilaa sitä lisäämällä tavoitteita."
        : "Poikkeuksellisen nopea. Järjestelmä epäilee mittausvirhettä.";
    speedButton.disabled = false;
    speedButton.innerHTML = `SUORITA UUDELLEEN ${icon("signal")}`;
    pushTelemetry(`Nopeustesti valmis: ${result.toFixed(1)} mot/s`);
  }, prefersReducedMotion.matches ? 50 : 1500);
});

const excuseButton = $<HTMLButtonElement>("#excuse-button");
const excuseOutput = $<HTMLElement>("#excuse-output");
let excuseIndex = 0;

excuseButton.addEventListener("click", () => {
  excuseIndex = (excuseIndex + Math.floor(Math.random() * (excuses.length - 1)) + 1) % excuses.length;
  excuseOutput.textContent = `“${excuses[excuseIndex]}”`;
  pushTelemetry("Uusi tekosyy haettu varapalvelimelta");
});

const updateClock = () => {
  const now = new Date();
  clock.dateTime = now.toISOString();
  clock.textContent = now.toLocaleTimeString("fi-FI", { hour12: false });
};

updateClock();
window.setInterval(updateClock, 1000);

const initSignalCanvas = () => {
  const canvas = $<HTMLCanvasElement>("#signal-canvas");
  const gl = canvas.getContext("webgl2", { alpha: false, antialias: false });
  if (!gl) {
    canvas.classList.add("canvas-fallback");
    return;
  }

  const vertexSource = `#version 300 es
    in vec2 a_position;
    void main() { gl_Position = vec4(a_position, 0.0, 1.0); }
  `;
  const fragmentSource = `#version 300 es
    precision highp float;
    out vec4 outColor;
    uniform vec2 u_resolution;
    uniform float u_time;
    uniform float u_signal;
    uniform float u_tuning;

    float hash(vec2 p) {
      return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
    }

    void main() {
      vec2 uv = gl_FragCoord.xy / u_resolution.xy;
      vec2 p = uv * 2.0 - 1.0;
      p.x *= u_resolution.x / u_resolution.y;
      float t = u_time * 0.18;
      float scan = sin(gl_FragCoord.y * 1.52 + u_time * 7.0) * 0.022;
      float grain = (hash(gl_FragCoord.xy + floor(u_time * 18.0)) - 0.5) * 0.09;
      float sweep = smoothstep(0.025, 0.0, abs(fract(uv.y - t) - 0.5)) * 0.13;
      float radial = exp(-2.8 * length(p - vec2(-0.32, 0.02)));
      vec3 base = vec3(0.020, 0.027, 0.023);
      vec3 blue = vec3(0.02, 0.30, 0.39) * radial * (0.12 + u_signal * 0.65);
      vec3 amber = vec3(0.92, 0.47, 0.03) * sweep * (0.2 + u_tuning);
      float glitch = step(0.965, hash(vec2(floor(uv.y * 54.0), floor(u_time * 5.0)))) * u_tuning;
      vec3 color = base + blue + amber + scan + grain + glitch * vec3(0.8, 0.12, 0.04);
      float vignette = smoothstep(1.5, 0.25, length(p * vec2(0.68, 0.9)));
      outColor = vec4(color * (0.72 + 0.28 * vignette), 1.0);
    }
  `;

  const compile = (type: number, source: string) => {
    const shader = gl.createShader(type);
    if (!shader) throw new Error("Shaderin luonti epäonnistui.");
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(shader) ?? "Shader-virhe");
    return shader;
  };

  const program = gl.createProgram();
  if (!program) return;
  gl.attachShader(program, compile(gl.VERTEX_SHADER, vertexSource));
  gl.attachShader(program, compile(gl.FRAGMENT_SHADER, fragmentSource));
  gl.linkProgram(program);
  gl.useProgram(program);

  const buffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  const position = gl.getAttribLocation(program, "a_position");
  gl.enableVertexAttribArray(position);
  gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

  const resolutionLocation = gl.getUniformLocation(program, "u_resolution");
  const timeLocation = gl.getUniformLocation(program, "u_time");
  const signalLocation = gl.getUniformLocation(program, "u_signal");
  const tuningLocation = gl.getUniformLocation(program, "u_tuning");

  const resize = () => {
    const ratio = Math.min(window.devicePixelRatio, 1.5);
    const width = Math.floor(canvas.clientWidth * ratio);
    const height = Math.floor(canvas.clientHeight * ratio);
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
      gl.viewport(0, 0, width, height);
    }
  };

  const render = (time: number) => {
    resize();
    const currentSignal = (Number(signalPercent.textContent) || 0) / 100;
    const tuning = mode === "tuning" ? 1 : mode === "running" ? 0.25 : 0;
    gl.uniform2f(resolutionLocation, canvas.width, canvas.height);
    gl.uniform1f(timeLocation, prefersReducedMotion.matches ? 0 : time / 1000);
    gl.uniform1f(signalLocation, currentSignal);
    gl.uniform1f(tuningLocation, tuning);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    requestAnimationFrame(render);
  };

  requestAnimationFrame(render);
};

initSignalCanvas();
